import { useParams } from 'react-router';
import { useGetChartBESSQuery } from '@/features/bess/hooks/queries/[bess]/get-bess-charts.ts';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useMemo, useRef } from 'react';
import { enUS, ro } from 'date-fns/locale';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { cn } from '@/utils/utils.ts';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import type { Props as LabelProps } from 'recharts/types/component/Label';
import { format, Locale } from 'date-fns';
import { LastUpdateTimestamp } from '@/components/last-update-timestamp.tsx';

const EMPTY_SOC_DATA: never[] = [];

const METRIC_COLORS = {
  BATTERY_STATE_OF_CHARGE: '#AC99FB',
} as const;

const METRIC_KEYS = {
  BATTERY_STATE_OF_CHARGE: 'battery_state_of_charge',
} as const;

const CHART_METRICS = [
  {
    label: 'Battery State of Charge',
    color: METRIC_COLORS.BATTERY_STATE_OF_CHARGE,
    selected: true,
    dataKey: METRIC_KEYS.BATTERY_STATE_OF_CHARGE,
    axis: false,
    id: METRIC_KEYS.BATTERY_STATE_OF_CHARGE,
  },
];

export const BessStateOfChargeChart = ({ className }: { className?: string }) => {
  const { bessId } = useParams();

  const { data, isLoading, dataUpdatedAt } = useGetChartBESSQuery({ id: bessId });
  const chart = useMemo(() => data?.batterySoC ?? EMPTY_SOC_DATA, [data?.batterySoC]);

  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width:640px)');
  const chartRef = useRef<HTMLDivElement>(null);

  const locale = (i18n.language as 'ro' | 'en') === 'ro' ? ro : enUS;

  const translatedFields = useMemo(() => {
    return CHART_METRICS.map((field) => ({
      ...field,
      label: t(`metrics.${field.id}`),
    }));
  }, [t, CHART_METRICS]);

  const chartDate = useMemo(() => {
    if (!chart) return [];

    const processValue = (value: number | null, shouldNegate = false): number | null => {
      if (value === null) return null;
      const absValue = Math.abs(value);
      return shouldNegate ? -absValue : absValue;
    };

    return chart?.map((v) => {
      return {
        [METRIC_KEYS.BATTERY_STATE_OF_CHARGE]: processValue(v?.battery_state_of_charge),
        time: v?.time,
      };
    });
  }, [chart]);

  return (
    <DashboardCard
      className={cn(className, 'flex min-h-[480px] w-full flex-col gap-2 select-none')}
      isLoading={isLoading}
    >
      <div className="flex w-full flex-col">
        <h3 className="font-semibold text-nowrap">State of Charge last 24 hours</h3>
        <LastUpdateTimestamp updatedAt={dataUpdatedAt} />
      </div>

      {!chart?.length ? (
        <div className="flex h-full min-h-[238px] w-full items-center justify-center rounded-md bg-gray-50">
          <span className="text-muted-foreground">
            {t('devices.batteryModule.noDataFound')}
          </span>
        </div>
      ) : (
        <>
          <div className={cn(`relative w-full`, isMobile ? 'h-[380px]' : 'h-[420px]')}>
            <div className="absolute top-3 right-12 z-10"></div>
            <div className="h-full w-full">
              <div className="h-full" ref={chartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chartDate}
                    margin={{
                      top: 20,
                      right: 0,
                      bottom: 0,
                      left: isMobile ? -15 : 0,
                    }}
                  >
                    <defs>
                      {translatedFields.map((v) => (
                        <linearGradient key={v.id} id={v.id} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={v.color} stopOpacity={1} />
                          <stop offset="95%" stopColor={v.color} stopOpacity={0.2} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid vertical={false} stroke="#e5e7eb" />
                    <XAxis
                      key="xAxis-main"
                      dataKey="time"
                      xAxisId="main"
                      minTickGap={isMobile ? 0 : 40}
                      tickFormatter={(t) => {
                        return formatDate(t, locale);
                      }}
                      tickLine={false}
                      axisLine={false}
                      tickMargin={4}
                      style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                    />
                    <YAxis
                      yAxisId="left"
                      label={{
                        value: '%',
                        angle: -90,
                        position: 'left',
                        offset: isMobile ? -20 : -10,
                      }}
                      style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                    />
                    <Tooltip
                      cursor={false}
                      content={(props) => <ChartTooltipComponent {...props} locale={locale} />}
                    />

                    {translatedFields.map((v, index) => {
                      if (!v.selected) return null;

                      return (
                        <Area
                          xAxisId="main"
                          yAxisId="left"
                          name={t ? t(`metrics.${v.dataKey}`) : v.label}
                          key={`area-${v.dataKey}-${index}`}
                          dataKey={v.dataKey}
                          stroke={v.color}
                          fill={`url(#${v.id})`}
                          fillOpacity={0.3}
                          label={(props: LabelProps) => {
                            const { x, y, value, index } = props;

                            if (
                              x === undefined ||
                              y === undefined ||
                              index === undefined ||
                              index % 3 !== 0 ||
                              index === 0
                            ) {
                              return null;
                            }

                            return (
                              <g>
                                <text
                                  x={x}
                                  y={y}
                                  dy={-6}
                                  fontSize={isMobile ? 14 : 16}
                                  fill="white"
                                  stroke="white"
                                  strokeWidth={3}
                                  strokeLinejoin="round"
                                  paintOrder="stroke"
                                  textAnchor="middle"
                                  style={{ pointerEvents: 'none' }}
                                >
                                  {value ? value : null}
                                </text>
                                <text
                                  x={x}
                                  y={y}
                                  dy={-6}
                                  fontSize={isMobile ? 14 : 16}
                                  fill="#7D7D85"
                                  textAnchor="middle"
                                  style={{ pointerEvents: 'none' }}
                                >
                                  {value ? value : null}
                                </text>
                              </g>
                            );
                          }}
                        />
                      );
                    })}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div className="mx-auto flex flex-wrap gap-1 md:gap-1">
            {translatedFields.map((f) => {
              return (
                <span
                  key={f.dataKey}
                  style={{ opacity: !f.selected ? '0.4' : '1' }}
                  className="h-min w-min justify-start p-1 px-2"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="size-4 rounded-full"
                      style={{ backgroundColor: f.color }}
                    ></span>
                    <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
                      {f.label}
                    </span>
                  </div>
                </span>
              );
            })}
          </div>
        </>
      )}
    </DashboardCard>
  );
};

const ChartTooltipComponent = ({
  payload,
  locale,
}: TooltipContentProps<ValueType, NameType> & {
  locale: Locale;
}) => {
  const time = payload?.[0]?.payload?.time;
  const formattedDate = time ? formatDate(new Date(time), locale) : '';

  const formatValue = (p: Payload<ValueType, NameType>): string => {
    if (p?.dataKey && p.dataKey === 'battery_state_of_charge') return `${p.value} %`;

    if (!p?.value) return '0 kWh';

    const value = Number(p.value);
    const fixedValue = +value.toFixed(2);
    const isBatteryPower = p.name === 'Battery Power';

    if (value < 1000) {
      return isBatteryPower ? `${fixedValue} kWh` : `${Math.abs(fixedValue)} kWh`;
    }

    const megaWattValue = (value / 1000).toFixed(2);
    return isBatteryPower ? `${megaWattValue} MWh` : `${Math.abs(Number(megaWattValue))} MWh`;
  };

  return (
    <div className="flex flex-col border border-gray-300 bg-white p-4 text-base">
      <p>{formattedDate}</p>
      <div className="mt-2">
        {payload?.map((p: Payload<ValueType, NameType>) => (
          <p style={{ color: p.stroke }} key={p.name}>
            {p.name} : {formatValue(p)}
          </p>
        ))}
      </div>
    </div>
  );
};

function formatDate(date: Date, locale?: Locale): string {
  const capitalizeWords = (str: string) =>
    str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  return capitalizeWords(format(date, 'MMM d, HH:mm', { locale }));
}
